import { createTheme, alpha } from '@mui/material/styles';

// Single source of truth for colors: components should reference palette tokens
// (e.g. 'background.paper', 'divider', 'text.secondary', 'success.light') instead of hex values.
const palette = {
  mode: 'dark',
  primary: {
    main: '#3b82f6', // Bright modern blue
    light: '#60a5fa',
    dark: '#2563eb',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#94a3b8',
    light: '#cbd5e1',
    dark: '#64748b',
    contrastText: '#0f172a',
  },
  background: {
    default: '#16181d', // Base dark background
    paper: '#1e2229', // Flat card/panel surface
  },
  text: {
    primary: '#f1f5f9', // Crisp light slate
    secondary: '#94a3b8', // Muted slate text
    disabled: '#64748b',
  },
  divider: '#2e3440', // Subtle flat border
  success: {
    main: '#34d399',
    light: 'rgba(52, 211, 153, 0.15)', // muted tint for chips/alerts
    contrastText: '#6ee7b7', // text color on the tint
  },
  warning: {
    main: '#fbbf24',
    light: 'rgba(251, 191, 36, 0.15)',
    contrastText: '#fde68a',
  },
  error: {
    main: '#f87171',
    light: 'rgba(248, 113, 113, 0.15)',
    contrastText: '#fca5a5',
  },
};

// Muted status chip: tinted background, readable text, hairline border
const tintedChip = (color) => ({ theme }) => ({
  backgroundColor: theme.palette[color].light,
  color: theme.palette[color].contrastText,
  border: `1px solid ${alpha(theme.palette[color].main, 0.3)}`,
});

const modernDarkFlatTheme = createTheme({
  palette,
  typography: {
    fontFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      letterSpacing: '-0.01em',
    },
    subtitle1: {
      color: palette.text.secondary,
      fontSize: '0.925rem',
    },
    subtitle2: {
      color: palette.text.secondary,
      fontWeight: 500,
      fontSize: '0.8rem',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: Array(25).fill('none'),
  components: {
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 10,
          backgroundImage: 'none',
        }),
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 10,
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '8px 16px',
        },
        outlined: ({ theme }) => ({
          borderColor: theme.palette.divider,
          color: theme.palette.text.primary,
          backgroundColor: 'transparent',
          '&:hover': {
            borderColor: theme.palette.secondary.dark,
            backgroundColor: theme.palette.action.hover,
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
        colorSuccess: tintedChip('success'),
        colorWarning: tintedChip('warning'),
        colorError: tintedChip('error'),
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundImage: 'none',
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
          padding: '12px 16px',
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.text.primary,
        }),
        head: ({ theme }) => ({
          fontWeight: 600,
          color: theme.palette.text.secondary,
          backgroundColor: theme.palette.background.default,
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          letterSpacing: '0.04em',
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.divider,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.dark,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: '1.5px',
          },
        }),
      },
    },
  },
});

export default modernDarkFlatTheme;
